/**
 * API Configuration for Thaklis Integration
 */

const apiConfig = {
  // Thaklis API Base URL
  // In development with proxy, use empty string for relative URLs
  // In production, this will be the full URL
  baseURL: process.env.REACT_APP_THAKLIS_API_URL !== undefined
    ? process.env.REACT_APP_THAKLIS_API_URL
    : '',

  // Note: Authentication uses JWT tokens obtained via /api/auth/login
  // Tokens are stored in localStorage and added to requests via interceptor

  // API Endpoints
  endpoints: {
    // Authentication
    login: '/api/auth/login',
    logout: '/api/auth/logout',

    // Typing Centers
    typingCenters: '/api/typing-centers',
    typingCenterById: (id) => `/api/typing-centers/${id}`,
    typingCenterUsers: (id) => `/api/users/typing-center/${id}`,

    // Organizations (Thaklis API)
    organizations: '/api/organisations',
    organizationsByType: (type) => `/api/organisations?type=${type}`,
    organizationCustomers: '/api/organisations',
    organizationDetails: (organizationId) => `/api/organisations/${organizationId}`,
    organizationKeyValues: (type) => `/api/organisations/keyValues?type=${type}`,
    organizationLogo: (orgId) => `/api/organizations/${orgId}/logo`,

    // Typing Center Mappings

    // Organization Configuration (per typing center mapping)
    organizationConfig: (mappingId) => `/api/mappings/${mappingId}/config`,
    organizationConfigByTcAndOrg: (typingCenterId, organizationId) => `/api/mappings/typing-center/${typingCenterId}/organization/${organizationId}/config`,

    // Organization Credentials (Thaklis auth per mapping)
    organizationCredentials: (mappingId) => `/api/mappings/${mappingId}/credentials`,
    organizationCredentialStatus: (mappingId) => `/api/mappings/${mappingId}/credentials/status`,
    organizationCredentialTest: (mappingId) => `/api/mappings/${mappingId}/credentials/test`,
    organizationCredentialToken: (mappingId) => `/api/mappings/${mappingId}/credentials/token`,

    // Tickets
    tickets: '/api/tickets',
    ticketById: (id) => `/api/tickets/${id}`,

    // Billing
    wallets: '/api/wallets',
    payments: '/api/payments',
    customerPayments: (customerId) => `/api/payments/customer/${customerId}`,

    // Dashboard
    dashboardStats: '/api/dashboard/stats',
    dashboardFinancialSummary: '/api/dashboard/financial-summary',
    dashboardTopServices: '/api/dashboard/top-services',
    dashboardTodaySummary: '/api/dashboard/today-summary',
    dashboardOperationsSummary: '/api/dashboard/operations-summary',
    invoiceAging: '/api/invoices/aging',
    recentActivity: '/api/dashboard/recent-activity',

    // Organization Payments (Pro Portal)
    organizationPayments: '/api/payments',
    paymentsViaProSummary: '/api/payments/via-pro-summary',
    organizationPaymentById: (id) => `/api/payments/${id}`,
    createOrganizationPayment: '/api/payments',
    updateOrganizationPayment: (id) => `/api/payments/${id}`,
    topupWalletFromPayment: (id) => `/api/payments/${id}/topup`,
    typingCenterPaymentSummary: (typingCenterId) => `/api/payments/typing-center/${typingCenterId}/summary`,
    typingCenterPaymentsByOrganization: (typingCenterId) => `/api/payments/typing-center/${typingCenterId}/by-organization`,

    // Thaklis bank accounts (proxy to Thaklis Main)
    availableBankAccounts: '/api/thaklis/bank-accounts',

    // Typing Center Bank Accounts (Managers-owned)
    tcBankAccounts: '/api/tc-bank-accounts',
    tcBankAccountById: (id) => `/api/tc-bank-accounts/${id}`,
    tcBankAccountSetDefault: (id) => `/api/tc-bank-accounts/${id}/set-default`,
    tcBankAccountActive: (id) => `/api/tc-bank-accounts/${id}/active`,
    tcBankTransfers: '/api/tc-bank-accounts/transfers',
    tcBankTransferReverse: (transferId) => `/api/tc-bank-accounts/transfers/${transferId}/reverse`,

    // Customer Wallets (one per internal organization)
    customerWallets: '/api/customer-wallets',
    customerWalletById: (id) => `/api/customer-wallets/${id}`,
    customerWalletByOrg: (orgId) => `/api/customer-wallets/organization/${orgId}`,
    customerWalletSummary: '/api/customer-wallets/summary',
    customerWalletsPendingRefund: '/api/customer-wallets/pending-refund',
    customerWalletRefund: '/api/customer-wallets/refund',
    customerWalletTransactions: (id) => `/api/customer-wallets/${id}/transactions`,
    customerWalletTransactionsSummary: (id) => `/api/customer-wallets/${id}/transactions/summary`,

    // Wallet Top-Up Requests (Managers)
    topupRequests: '/api/topup-requests',
    topupRequestById: (id) => `/api/topup-requests/${id}`,
    topupRequestApprove: (id) => `/api/topup-requests/${id}/approve`,
    topupRequestReject: (id) => `/api/topup-requests/${id}/reject`,
    topupRequestProofUrl: (id) => `/api/topup-requests/${id}/proof-url`,

    // Wallet Top-Up Requests (Portal)
    portalTopUpSubmit: '/api/portal/wallet/topup',
    walletTopUpSubmit: (walletId) => `/api/topup-requests/wallets/${walletId}`,
    portalTopUpRequests: '/api/portal/wallet/topup-requests',
    portalTopUpCancel: (id) => `/api/portal/wallet/topup-requests/${id}`,
    portalWallet: '/api/portal/wallet',
    portalWalletBankAccounts: '/api/portal/wallet/bank-accounts',
    workOrderTasks: '/api/tasks/work-order-tasks',
    workOrderTaskStatistics: '/api/tasks/work-order-tasks/statistics',
    portalTasks: '/api/portal/tasks',
    portalTaskById: (id) => `/api/portal/tasks/${id}`,
    portalTaskEmployees: '/api/portal/tasks/employees',
    portalTaskDocuments: (id) => `/api/portal/tasks/${id}/documents`,
    portalTaskDocumentDownload: (id, documentId) => `/api/portal/tasks/${id}/documents/${documentId}/download`,

    // Finance Overview (Ticket Payments)
    ticketOverview: '/api/finance/ticket-overview',

    // Invoice Configuration
    invoiceConfig: (typingCenterId) => `/api/invoice-config/${typingCenterId}`,
    workOrderConfig: (typingCenterId) => `/api/work-order-config/${typingCenterId}`,

    // Client (internal org) employees + their documents (proxied from Thaklis Main)
    clientEmployees: (orgId) => `/api/internal-organizations/${orgId}/employees`,
    clientEmployeeDocuments: (orgId, uid) => `/api/internal-organizations/${orgId}/employees/${uid}/documents`,
    clientEmployeeDocumentView: (orgId) => `/api/internal-organizations/${orgId}/employees/documents/view`,
    clientEmployeeCreate: (orgId) => `/api/internal-organizations/${orgId}/employees`,
    clientEmployeeStatus: (orgId, uid) => `/api/internal-organizations/${orgId}/employees/${uid}/status`,
    clientEmployeeDocumentUpload: (orgId, uid) => `/api/internal-organizations/${orgId}/employees/${uid}/documents`,
    clientEmployeeDocumentVersionDelete: (orgId, uid, groupId) => `/api/internal-organizations/${orgId}/employees/${uid}/documents/${groupId}`,
    invoiceConfigLogo: (typingCenterId) => `/api/invoice-config/${typingCenterId}/logo`,
    invoiceConfigPartnerLogo: (typingCenterId) => `/api/invoice-config/${typingCenterId}/partner-logo`,
    allInvoiceConfigs: '/api/invoice-config/all/list',

    // Invoices
    storageTypingCenters: '/api/storage/typing-centers',
    storageRecompute: '/api/storage/typing-centers/recompute',
    lastActiveUsers: '/api/activity/last-active',
    invoices: '/api/invoices',
    invoiceById: (id) => `/api/invoices/${id}`,
    invoiceStatistics: '/api/invoices/statistics',
    invoiceTickets: (id) => `/api/invoices/${id}/tickets`,
    invoiceFinalize: (id) => `/api/invoices/${id}/finalize`,
    invoiceVoid: (id) => `/api/invoices/${id}/void`,
    invoiceMarkPaid: (id) => `/api/invoices/${id}/mark-paid`,
    invoicePdf: (id) => `/api/invoices/${id}/pdf`,
    checkTicketInvoiced: (ticketId) => `/api/invoices/check-ticket/${ticketId}`,
    invoicedTickets: (organizationId) => `/api/invoices/invoiced-tickets/${organizationId}`,

    // Quotations
    quotations: '/api/quotations',
    quotationById: (id) => `/api/quotations/${id}`,
    quotationStatistics: '/api/quotations/statistics',
    quotationFinalize: (id) => `/api/quotations/${id}/finalize`,
    quotationSend: (id) => `/api/quotations/${id}/send`,
    quotationAccept: (id) => `/api/quotations/${id}/accept`,
    quotationReject: (id) => `/api/quotations/${id}/reject`,
    quotationConvert: (id) => `/api/quotations/${id}/convert`,

    // Work Orders
    workOrders: '/api/work-orders',
    workOrderById: (id) => `/api/work-orders/${id}`,
    workOrderStatistics: '/api/work-orders/statistics',
    workOrderFromQuotation: (quotationId) => `/api/work-orders/from-quotation/${quotationId}`,
    workOrderStart: (id) => `/api/work-orders/${id}/start`,
    workOrderHold: (id) => `/api/work-orders/${id}/hold`,
    workOrderComplete: (id) => `/api/work-orders/${id}/complete`,
    workOrderCancel: (id) => `/api/work-orders/${id}/cancel`,
    workOrderMarkInvoiced: (id) => `/api/work-orders/${id}/mark-invoiced`,
    workOrderTasks: (id) => `/api/work-orders/${id}/tasks`,
    workOrderTaskById: (id, taskId) => `/api/work-orders/${id}/tasks/${taskId}`,
    workOrderTaskComplete: (id, taskId) => `/api/work-orders/${id}/tasks/${taskId}/complete`,
    workOrderTaskSendEmail: (id, taskId) => `/api/work-orders/${id}/tasks/${taskId}/send-email`,
    workOrderSuggestedRecipients: (id) => `/api/work-orders/${id}/suggested-recipients`,
    workOrderDocuments: (id) => `/api/work-orders/${id}/documents`,
    workOrderDocumentById: (id, documentId) => `/api/work-orders/${id}/documents/${documentId}`,
    workOrderDocumentDownload: (id, documentId) => `/api/work-orders/${id}/documents/${documentId}/download`,

    // Email Configuration (Global)
    emailConfig: '/api/email-config',
    emailConfigTemplates: '/api/email-config/templates',
    emailConfigTemplateByType: (type) => `/api/email-config/templates/${type}`,
    emailConfigTest: '/api/email-config/test',
    emailConfigTemplateTypes: '/api/email-config/template-types',

    // AI Configuration (per typing center)
    aiConfig: '/api/ai-config',
    aiConfigTest: '/api/ai-config/test',
    notificationPreferences: '/api/notification-preferences',

    // Company Configuration (Global)
    companyConfig: '/api/company-config',
    companyConfigById: (typingCenterId) => `/api/company-config/${typingCenterId}`,
    companyConfigLogo: (typingCenterId) => `/api/company-config/${typingCenterId}/logo`
  },

  // Request timeout (in milliseconds)
  timeout: 60000,

  // Headers
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

export default apiConfig;
